import { AuthError, InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { FullStory, isInitialized as isFullstoryInitialized } from '@fullstory/browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

import analytics from '../services/analytics';
import {
  clearInteractiveLoginCooldown,
  isInteractiveLoginOnCooldown,
  markInteractiveLoginStarted,
  useAuthToken
} from './entraAuthConfig';
import { signInToAmp } from './signInToAmp';

const authenticationRequest = {
  scopes: ['User.Read'],
  forceRefresh: true
};

const REFRESH_TOKEN_QUERY_KEY = ['msal-auth-refresh-token-query'];
const REFRESH_TOKEN_INTERVAL_MS = 1000 * 60 * 30;
const TRANSIENT_AUTH_ERROR_CODES = [
  'user_cancelled',
  'user_canceled',
  'timed_out',
  'empty_window_error',
  'iframe_closed_prematurely',
  'block_iframe_reload',
  'interaction_in_progress',
  'no_network_connectivity',
  'network_error',
  'post_request_failed',
  'get_request_failed',
  'native_handshake_timeout'
];

const requiresInteractiveLogin = (error: unknown, hasCachedAccount: boolean): error is AuthError =>
  error instanceof AuthError && (!hasCachedAccount || !TRANSIENT_AUTH_ERROR_CODES.includes(error.errorCode));

const isDocumentVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const SIGN_IN_TIMEOUT_MS = 1000 * 20;

const useRefreshTokenEveryHalfHourQuery = ({
  acquireToken,
  enabled
}: {
  acquireToken: ReturnType<typeof useMsalAuthentication>['acquireToken'];
  enabled: boolean;
}) =>
  useQuery({
    queryKey: REFRESH_TOKEN_QUERY_KEY,
    queryFn: () => acquireToken(InteractionType.Silent, authenticationRequest),
    refetchInterval: REFRESH_TOKEN_INTERVAL_MS,
    refetchIntervalInBackground: true,
    staleTime: REFRESH_TOKEN_INTERVAL_MS,
    initialData: () => null as Awaited<ReturnType<typeof acquireToken>>,
    throwOnError: false,
    enabled
  });

const EntraUserAuthenticationState = ({
  isAuthenticatedToBackend,
  setIsAuthenticatedToBackend
}: {
  isAuthenticatedToBackend: boolean;
  setIsAuthenticatedToBackend: (isAuthenticatedToBackend: boolean) => void;
}) => {
  const { instance } = useMsal();
  const queryClient = useQueryClient();
  const [authResultFallback, setAuthResultFallback] = React.useState<Awaited<ReturnType<typeof acquireToken>>>(null);

  const navigate = useNavigate();

  const accountIdentifiers = React.useMemo(() => {
    const userEmailFromStorage = (JSON.parse(localStorage.getItem('authInfo') || '{}') as any).currentUserEmail as
      | string
      | undefined;

    if (userEmailFromStorage) {
      return {
        username: userEmailFromStorage
      };
    }
  }, []);

  // arguments MUST be stable values, see hook's implementation
  const { login, result, error, acquireToken } = useMsalAuthentication(
    InteractionType.Silent,
    authenticationRequest,
    accountIdentifiers
  );

  const { error: errorFromQuery } = useRefreshTokenEveryHalfHourQuery({
    acquireToken,
    enabled: isAuthenticatedToBackend
  });

  React.useEffect(() => {
    const derivedAuthResponse = result || authResultFallback;

    if (derivedAuthResponse) {
      instance.setActiveAccount(derivedAuthResponse.account);
      useAuthToken.setState({ idToken: derivedAuthResponse.idToken, provider: 'entra' });
      localStorage.setItem('entraIdToken', derivedAuthResponse.idToken);
    }

    if (derivedAuthResponse && !isAuthenticatedToBackend) {
      signInToAmp(setIsAuthenticatedToBackend);
    }
  }, [instance, authResultFallback, result, isAuthenticatedToBackend, setIsAuthenticatedToBackend]);

  const location = useLocation();
  const navigateRef = React.useRef(navigate);
  const locationRef = React.useRef(location);
  const interactiveLoginStarted = React.useRef(false);

  React.useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
  }, [navigate, location]);

  const startInteractiveLogin = React.useCallback(
    (derivedError: AuthError) => {
      if (interactiveLoginStarted.current || isInteractiveLoginOnCooldown()) {
        return;
      }

      interactiveLoginStarted.current = true;
      markInteractiveLoginStarted();

      // eslint-disable-next-line no-console
      console.error('AuthError', derivedError);
      isFullstoryInitialized() && FullStory('trackEvent', { name: 'AuthError', properties: { error: derivedError } });

      login(derivedError.errorCode === 'popup_window_error' ? InteractionType.Redirect : InteractionType.Popup, {
        ...authenticationRequest,
        prompt: 'select_account'
      })
        .then(loginResult => {
          // eslint-disable-next-line no-console
          console.log('Authentication successful');
          interactiveLoginStarted.current = false;
          clearInteractiveLoginCooldown();
          queryClient.resetQueries({ queryKey: REFRESH_TOKEN_QUERY_KEY });
          setAuthResultFallback(loginResult);
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('Authentication failed', err);
          setIsAuthenticatedToBackend(false);
          navigateRef.current('/login', {
            state: {
              from: `${locationRef.current.pathname}${locationRef.current.search}`,
              interactiveAuthFailed: true
            }
          });
        });
    },
    [login, queryClient, setIsAuthenticatedToBackend]
  );

  React.useEffect(() => {
    const derivedError = error || errorFromQuery;

    if (
      !requiresInteractiveLogin(derivedError, instance.getAllAccounts().length > 0) ||
      interactiveLoginStarted.current
    ) {
      return;
    }

    if (!isDocumentVisible()) {
      const onVisibilityChange = () => {
        if (isDocumentVisible()) {
          document.removeEventListener('visibilitychange', onVisibilityChange);
          startInteractiveLogin(derivedError);
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    startInteractiveLogin(derivedError);
  }, [instance, startInteractiveLogin, error, errorFromQuery]);

  React.useEffect(() => {
    if (isAuthenticatedToBackend) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (interactiveLoginStarted.current || instance.getAllAccounts().length > 0) {
        return;
      }

      navigateRef.current('/login', {
        state: {
          from: `${locationRef.current.pathname}${locationRef.current.search}`,
          interactiveAuthFailed: true
        }
      });
    }, SIGN_IN_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [instance, isAuthenticatedToBackend]);

  React.useEffect(() => {
    analytics.page(location);
  }, [location]);

  return <Outlet />;
};

export default EntraUserAuthenticationState;
